import React from "react";
import { MessageCircle, Phone, MapPin, Mail, ArrowRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const SERVICES = [
  "Instalación eléctrica",
  "Mantenimiento de comunidad",
  "Videoportero",
  "Fotovoltaica / baja tensión",
  "Punto de recarga VE",
  "Avería urgente",
  "Otro",
];

export default function Contact() {
  const [name, setName] = React.useState("");
  const [service, setService] = React.useState("");
  const [msg, setMsg] = React.useState("");
  const [phone, setPhone] = React.useState("");

  const buildLink = () => {
    const text =
      `Hola Moisés, soy ${name || "…"}.\n` +
      `Servicio: ${service || "por definir"}.\n` +
      (phone ? `Teléfono de contacto: ${phone}.\n` : "") +
      (msg ? `\n${msg}` : "\n¿Podríamos hablar?");
    return "https://wa.me/34617420491?text=" + encodeURIComponent(text);
  };

  return (
    <section id="contacto" className="relative py-24 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <span className="section-label mb-4">08 · Contacto</span>
          <h2 className="font-display text-4xl sm:text-5xl text-white mt-4 leading-[0.95]">
            Cuéntame qué <span className="text-[#E57C23]">necesitas.</span>
          </h2>
          <p className="mt-5 text-[#9CA3AF] leading-relaxed">
            Te respondo personalmente por WhatsApp normalmente en minutos. Sin
            call center, sin formularios que se pierden — la conversación va
            directa a mi móvil.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href="tel:+34617420491"
              data-testid="contact-phone"
              className="flex items-center gap-4 group"
            >
              <span className="h-12 w-12 rounded-xl border border-white/10 bg-[#121620] flex items-center justify-center group-hover:border-[#00E5FF]/60 group-hover:text-[#00E5FF] text-white transition-colors">
                <Phone size={18} />
              </span>
              <div>
                <div className="font-mono text-[11px] text-white/40 tracking-widest">TELÉFONO</div>
                <div className="font-display text-xl text-white group-hover:text-[#00E5FF] transition-colors">
                  617 420 491
                </div>
              </div>
            </a>
            <a
              href="https://wa.me/34617420491"
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp"
              className="flex items-center gap-4 group"
            >
              <span className="h-12 w-12 rounded-xl border border-white/10 bg-[#121620] flex items-center justify-center group-hover:border-[#E57C23]/60 group-hover:text-[#E57C23] text-white transition-colors">
                <MessageCircle size={18} />
              </span>
              <div>
                <div className="font-mono text-[11px] text-white/40 tracking-widest">WHATSAPP</div>
                <div className="font-display text-xl text-white group-hover:text-[#E57C23] transition-colors">
                  Escribir ahora
                </div>
              </div>
            </a>
            <div className="flex items-center gap-4">
              <span className="h-12 w-12 rounded-xl border border-white/10 bg-[#121620] flex items-center justify-center text-white">
                <MapPin size={18} />
              </span>
              <div>
                <div className="font-mono text-[11px] text-white/40 tracking-widest">TALLER</div>
                <div className="text-white/90">Calle Bilbao Nº53, Telde · 35220</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              window.open(buildLink(), "_blank");
            }}
            data-testid="contact-form"
            className="rounded-3xl border border-white/8 bg-[#121620]/70 p-6 sm:p-8 space-y-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-white/70 mb-2 block">Tu nombre</label>
                <Input
                  data-testid="contact-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nombre y apellido"
                  className="bg-[#0A0D14] border-white/10 text-white h-12"
                  required
                />
              </div>
              <div>
                <label className="text-sm text-white/70 mb-2 block">Teléfono (opcional)</label>
                <Input
                  data-testid="contact-phone-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="6XX XXX XXX"
                  className="bg-[#0A0D14] border-white/10 text-white h-12"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-white/70 mb-2 block">Servicio</label>
              <Select value={service} onValueChange={setService}>
                <SelectTrigger data-testid="contact-service" className="bg-[#0A0D14] border-white/10 h-12 text-white">
                  <SelectValue placeholder="Elige un servicio…" />
                </SelectTrigger>
                <SelectContent className="bg-[#121620] border-white/10 text-white">
                  {SERVICES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-white/70 mb-2 block">Cuéntame el trabajo</label>
              <Textarea
                data-testid="contact-message"
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                rows={5}
                placeholder="Describe qué necesitas, la zona y si es urgente…"
                className="bg-[#0A0D14] border-white/10 text-white resize-none"
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-white/40">
                Al enviar se abrirá WhatsApp con tu mensaje ya redactado.
              </p>
              <button
                type="submit"
                data-testid="contact-submit"
                className="btn-copper"
              >
                <MessageCircle size={16} strokeWidth={2.4} />
                Enviar por WhatsApp
                <ArrowRight size={15} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
